import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";

/** Promoter — submit a leave request + see own request list. */
export default function PromoterLeaveRequest() {
  const [form, setForm] = useState({ from_date: "", to_date: "", reason: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/promoter/attendance/leave-requests")
    .then(({ data }) => setRows(data))
    .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.from_date || !form.to_date) { toast.error("Pick the from / to dates"); return; }
    if (!form.reason.trim()) { toast.error("Please describe the reason"); return; }
    if (form.from_date > form.to_date) { toast.error("From date is after to date"); return; }
    setBusy(true);
    try {
      await api.post("/promoter/attendance/leave-request", form);
      toast.success("Leave request submitted");
      setForm({ from_date: "", to_date: "", reason: "" });
      load();
    } catch (e2) {
      toast.error("Could not submit", { description: formatApiErrorDetail(e2.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="leave-request-page">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Leave Request</h1>
        <p className="text-xs text-slate-500 mt-0.5">Your Product Manager will review the request.</p>
      </header>

      <Card>
        <CardContent className="p-4 space-y-3">
          <form onSubmit={submit} className="space-y-3" data-testid="leave-request-form">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[11px] uppercase font-semibold text-slate-600">From</Label>
                <Input type="date" value={form.from_date}
                       onChange={(e) => setForm((f) => ({ ...f, from_date: e.target.value }))}
                       data-testid="leave-from-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase font-semibold text-slate-600">To</Label>
                <Input type="date" value={form.to_date}
                       onChange={(e) => setForm((f) => ({ ...f, to_date: e.target.value }))}
                       data-testid="leave-to-date" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-semibold text-slate-600">Reason</Label>
              <Textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                        rows={3} placeholder="Brief reason..." data-testid="leave-reason" />
            </div>
            <Button type="submit" disabled={busy}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="leave-submit-btn">
              <CalendarPlus size={14} className="mr-1" /> {busy ? "Submitting..." : "Submit request"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3 space-y-2">
          <div className="font-semibold text-slate-900 text-sm px-1">Your requests</div>
          <div className="space-y-2">
            {rows.length === 0 && (
              <div className="text-xs text-slate-400 text-center py-4" data-testid="leave-empty">
                No leave requests yet.
              </div>
            )}
            {rows.map((r) => (
              <div key={r.id} className="border border-slate-100 rounded-md p-2 text-xs space-y-1"
                   data-testid={`leave-row-${r.id.slice(0, 8)}`}>
                <div className="flex justify-between items-center">
                  <div className="font-mono">{r.from_date} → {r.to_date}</div>
                  <StatusPill status={r.status} />
                </div>
                <div className="text-slate-600">{r.reason}</div>
                {r.decided_by && (
                  <div className="text-[10px] text-slate-500">
                    {r.status === "approved" ? "✓" : "✗"} {r.decided_by} · {(r.decided_at || "").split("T")[0]}
                    {r.decision_remarks ? ` · ${r.decision_remarks}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusPill({ status }) {
  const c = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
  }[status] || "bg-slate-100 text-slate-700";
  return <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${c}`}>{status}</span>;
}
